export const TERMINAL_STATE_NAMES = ["Checked", "Unchecked"] as const;
type TerminalStateNamesTuple = typeof TERMINAL_STATE_NAMES;
export type TerminalStateName = TerminalStateNamesTuple[number];

export const TRANSITIONAL_STATE_NAMES = ["Checking", "Unchecking", "Saving"] as const;
type TransitionalStateNamesTuple = typeof TRANSITIONAL_STATE_NAMES;
export type TransitionalStateName = TransitionalStateNamesTuple[number];

export const TRANSITIONAL_ERROR_STATE_NAMES = [
  "CheckingError",
  "UncheckingError",
  "SavingError",
] as const;
type TransitionalErrorStateNamesTuple = typeof TRANSITIONAL_ERROR_STATE_NAMES;
export type TransitionalErrorStateName = TransitionalErrorStateNamesTuple[number];

export type ItemStateName =
  | "EditingNew"
  | TerminalStateName
  | TransitionalStateName
  | TransitionalErrorStateName;

export type ItemState = {
  name: ItemStateName;
  messageTitle?: string;
  messageContent?: string;
};

export type ItemStateMap = {
  [key: number]: ItemState | undefined;
};

export const NEW_ITEM_KEY = 0;

export function assignItemState(
  itemStateMap: ItemStateMap,
  name: ItemStateName | undefined,
  keys: number | number[]
) {
  return {
    ...itemStateMap,
    ...(Array.isArray(keys) ? keys : [keys]).reduce(
      (is, key) => ({
        ...is,
        [key]: name === undefined ? undefined : { name },
      }),
      {}
    ),
  };
}

export function getItemState(itemStateMap: ItemStateMap, itemKey: number) {
  return (itemStateMap || {})[itemKey];
}

export function hasStateName(itemState: ItemState | undefined, ...names: ItemStateName[]): boolean {
  if (!itemState) {
    return false;
  }
  return names.find((name) => name === itemState.name) !== undefined;
}

export function hasItemStateWithName(
  itemStateMap: ItemStateMap,
  itemKey: number,
  ...names: ItemStateName[]
) {
  const itemState = getItemState(itemStateMap, itemKey);
  return hasStateName(itemState, ...names);
}

export function isErrorState(itemState: ItemState | undefined): boolean {
  return hasStateName(itemState, ...TRANSITIONAL_ERROR_STATE_NAMES);
}

export function isCheckedState(itemState: ItemState | undefined): boolean {
  return hasStateName(itemState, "Checked");
}

export function isUncheckedState(itemState: ItemState | undefined): boolean {
  return hasStateName(itemState, "Unchecked");
}

export function isTransitionalState(itemState: ItemState | undefined): boolean {
  return hasStateName(itemState, ...TRANSITIONAL_STATE_NAMES);
}

export function isCheckedOrTransitionalState(itemState: ItemState | undefined): boolean {
  return isCheckedState(itemState) || isTransitionalState(itemState);
}

export function isEditingNewState(itemState: ItemState | undefined): boolean {
  return hasStateName(itemState, "EditingNew");
}

const TRANSITION_TIP: { [p in TransitionalStateName]: string } = {
  ["Checking"]: "Selecting...",
  ["Unchecking"]: "Deselecting...",
  ["Saving"]: "Saving...",
};

export function getTransitionTip(itemState: ItemState | undefined): string | undefined {
  if (!itemState) {
    return undefined;
  }
  const name = itemState.name as TransitionalStateName;
  return TRANSITION_TIP[name];
}
